// src/containers/Repertoire/Utils.tsx
import { useEffect, useRef, useState, useCallback } from 'react';

function throttle<T extends (...args: unknown[]) => void>(fn: T, wait = 150) {
  let last = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;
  return (...args: Parameters<T>) => {
    const now = Date.now();
    const remaining = wait - (now - last);
    if (remaining <= 0) {
      last = now;
      fn(...args);
    } else {
      clearTimeout(timer);
      timer = setTimeout(() => {
        last = Date.now();
        fn(...args);
      }, remaining);
    }
  };
}

function useTheaterUI(opts: {
  isNative: boolean;
  inPerformanceMode: boolean;
  containerRef?: React.RefObject<HTMLDivElement> | React.MutableRefObject<HTMLDivElement | null>;
}) {
  const { isNative, inPerformanceMode, containerRef } = opts;
  const [headerVisible, setHeaderVisible] = useState(true);
  const autoHideTimerRef = useRef<number | null>(null);
  const lastScrollTopRef = useRef(0);
  const touchStartYRef = useRef<number | null>(null);
  const pinnedByTapRef = useRef(false);

  const clearTimer = () => {
    if (autoHideTimerRef.current) {
      window.clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
  };

  const showHeaderTemporarily = useCallback(
    (ms = 3000) => {
      pinnedByTapRef.current = false;
      setHeaderVisible(true);
      clearTimer();
      autoHideTimerRef.current = window.setTimeout(() => {
        // Only auto-hide if still in performance mode
        if (inPerformanceMode) setHeaderVisible(false);
      }, ms);
    },
    [inPerformanceMode],
  );

  useEffect(() => {
    if (!isNative || !inPerformanceMode || !containerRef?.current) return;
    const el = containerRef.current;
    const onTouchStart = (e: TouchEvent) => {
      const y = e.touches?.[0]?.clientY ?? 0;
      touchStartYRef.current = y;
      if (y <= 32) showHeaderTemporarily();
    };

    const onTouchMove = throttle((e: TouchEvent) => {
      const y = e.touches?.[0]?.clientY ?? 0;
      const startY = touchStartYRef.current ?? y;
      const dy = y - startY;
      // Pulling down (positive dy) => reveal
      if (dy > 10) showHeaderTemporarily();
    }, 80);

    const onTouchEnd = () => {
      touchStartYRef.current = null;
    };

    const onScroll = throttle(() => {
      const st = el.scrollTop;
      const goingUp = st < lastScrollTopRef.current - 2; // user scrolled up
      const goingDown = st > lastScrollTopRef.current + 2; // user scrolled down
      lastScrollTopRef.current = st;

      if (goingUp) showHeaderTemporarily();
      if (goingDown && inPerformanceMode && !pinnedByTapRef.current) {
        setHeaderVisible(false);
      }
    }, 100);

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('scroll', onScroll, { passive: true });

    const onClick = () => {
      clearTimer();
      if (!headerVisible) {
        pinnedByTapRef.current = true; // pin until next tap
        setHeaderVisible(true);
      } else {
        pinnedByTapRef.current = false; // we're hiding now
        autoHideTimerRef.current = window.setTimeout(() => {
          if (inPerformanceMode) setHeaderVisible(false);
        }, 120);
      }
    };
    el.addEventListener('click', onClick);

    return () => {
      el.removeEventListener('touchstart', onTouchStart as EventListener);
      el.removeEventListener('touchmove', onTouchMove as EventListener);
      el.removeEventListener('touchend', onTouchEnd as EventListener);
      el.removeEventListener('scroll', onScroll as EventListener);
      el.removeEventListener('click', onClick as EventListener);
    };
  }, [isNative, inPerformanceMode, containerRef, headerVisible, showHeaderTemporarily]);

  const hideHeaderSoon = useCallback(
    (ms = 250) => {
      pinnedByTapRef.current = false;
      clearTimer();
      autoHideTimerRef.current = window.setTimeout(() => {
        if (inPerformanceMode) setHeaderVisible(false);
      }, ms);
    },
    [inPerformanceMode],
  );

  // Clear timer on unmount or mode change
  useEffect(() => () => clearTimer(), []);
  useEffect(() => {
    clearTimer();
    if (!inPerformanceMode) {
      pinnedByTapRef.current = false;
      setHeaderVisible(true);
    }
    // entering perf mode: leave current visibility as-is
  }, [inPerformanceMode]);

  return { headerVisible, setHeaderVisible, showHeaderTemporarily, hideHeaderSoon };
}

export { useTheaterUI };
